/**
 * BLU-1506 "Order and wait": the Order Queue's Waiting on blank view, for the
 * floor's owner. Opened in place by the amber "Waiting on blank (N)" button
 * (`?view=waiting_blank`, `waitingOnBlankHref`), it lists the orders that wait
 * for a blank from a supplier, grouped by PO, then blank, in ONE table so the
 * columns line up across groups (DESIGN.md section 14.1).
 *
 *   PO row     status, "Next {supplier} purchase order" (still being built)
 *              or "{supplier}" (sent), the PO number (a link for PO viewers),
 *              Send by / Sent, the whole PO's blanks and orders, and Arrived
 *              on a sent PO for PO managers (the purchase-orders feature's own
 *              ArrivedDialog: one dialog, one preview, one toast). The table
 *              scrolls sideways on a phone; this row does not: it is pinned
 *              to the visible width of the scroll box (a size container, so
 *              `100cqw`), stacked below `sm`, so Arrived is always on screen.
 *   blank row  the blank's name and SKU.
 *   order row  order, client (3PL floors), ordered, qty, status.
 *
 * The server searches, counts and pages ORDERS (`getWaitingOnBlank`); this
 * view only arranges the page it is given. The PO headers (supplier names,
 * Send by, whole-PO counts, Arrived's lines) come from the To buy read, which
 * only PO viewers may make; without it the view still lists every order, with
 * the PO's number and its rows' own statuses.
 */
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { CheckCheck, ChevronLeft, ChevronRight, PackageCheck, Search } from 'lucide-react';
import type { OrderAndWaitPo } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { SectionError } from '@/components/shared/SectionError';
import { ClientOrgBadge } from '@/components/ClientOrgBadge';
import { useRole } from '@/hooks/use-role';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDayMonth, useDateFnsLocale } from '@/lib/format-dates';
import { cn } from '@/lib/utils';
import { ArrivedDialog } from '@/features/purchase-orders/components/ArrivedDialog';
import { OrderAndWaitStatusChip } from '@/features/purchase-orders/components/OrderAndWaitStatusChip';
import { PoNumberLink } from '@/features/purchase-orders/components/PoNumberLink';
import { useOrderAndWaitPos } from '@/features/purchase-orders/hooks/useOrderAndWait';
import {
  ARRIVED_BUTTON_TONE,
  formatSendTime,
  nextBuildingPoNumber,
  sendByState,
  supplierKey,
  suppliersSentToday,
} from '@/features/purchase-orders/lib/order-and-wait';
import { jitRowName } from '@/features/purchase-orders/lib/jit-lines';
import { WAITING_PAGE_SIZE, useWaitingOnBlankList } from '../hooks/useWaitingOnBlank';
import { groupWaitingRows, type WaitingPoGroup } from '../utils/waiting-on-blank';

/** DESIGN.md section 3.4 small buttons. */
const SMALL_BUTTON = 'h-8 gap-1.5 px-3 text-xs';
/** The PO row's buttons: the same, with the phone-sized hit target the To buy cards use. */
const GROUP_BUTTON = 'h-10 gap-1.5 px-3 text-xs sm:h-8';
const ARRIVED_BUTTON = cn(GROUP_BUTTON, ARRIVED_BUTTON_TONE);

interface WaitingOnBlankListProps {
  locationId: string;
  /** The floor's IANA timezone: Send by is the floor's clock. */
  timezone: string | null;
  ownOrgId: string | undefined;
  /** Client names at this floor (the queue's one roster). */
  orgIdToName: ReadonlyMap<string, string>;
  /** The view's search, as the URL holds it. */
  search: string;
  onSearchChange: (value: string) => void;
  /** Leave the view (back to the queue). */
  onClose: () => void;
}

export function WaitingOnBlankList({
  locationId,
  timezone,
  ownOrgId,
  orgIdToName,
  search,
  onSearchChange,
  onClose,
}: WaitingOnBlankListProps) {
  const { t } = useTranslation('production');
  const { can } = useRole();
  const canViewPos = can('purchase_orders.view');
  const canManagePos = can('purchase_orders.manage');

  // The box is the URL's from the first render; typing writes back after 300 ms.
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  useEffect(() => {
    if (debouncedSearch !== search) onSearchChange(debouncedSearch);
  }, [debouncedSearch, search, onSearchChange]);

  // A new search starts at page 1 (derived, so no effect and no stale page).
  const [paging, setPaging] = useState({ search, page: 0 });
  const page = paging.search === search ? paging.page : 0;
  const goToPage = (next: number) => setPaging({ search, page: Math.max(0, next) });

  const list = useWaitingOnBlankList(locationId, search, page);
  const pos = useOrderAndWaitPos(canViewPos ? locationId : null);

  // Someone who does not run this floor has nothing here: back to the queue.
  const notOwner = list.data?.isOwner === false;
  useEffect(() => {
    if (notOwner) onClose();
  }, [notOwner, onClose]);

  const headers = useMemo(
    () => new Map(pos.purchaseOrders.map((po) => [po.poId, po] as const)),
    [pos.purchaseOrders],
  );
  const rows = useMemo(() => list.data?.rows ?? [], [list.data?.rows]);
  const groups = useMemo(() => groupWaitingRows(rows, headers), [rows, headers]);
  // A supplier whose order went out today sends next by the time tomorrow.
  const sentTodayKeys = useMemo(
    () => suppliersSentToday(pos.purchaseOrders, timezone),
    [pos.purchaseOrders, timezone],
  );
  const showClient = rows.some((row) => !!row.organizationId && row.organizationId !== ownOrgId);

  // Arrived: the PO's header from the To buy read, and the supplier's PO that
  // is still being built (where short blanks go next).
  const [arrivingPoId, setArrivingPoId] = useState<string | null>(null);
  const arriving = arrivingPoId ? (headers.get(arrivingPoId) ?? null) : null;
  const arrivingNext = arriving ? nextBuildingPoNumber(pos.purchaseOrders, arriving) : null;

  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / WAITING_PAGE_SIZE));
  const searching = search.trim().length > 0;

  let body: ReactNode;
  if (list.isLoading || (!list.data && !list.error)) {
    body = <WaitingSkeleton />;
  } else if (!list.data) {
    body = (
      <SectionError
        title={t('orders.waiting.loadError')}
        body={t('orders.waiting.loadErrorBody')}
        onRetry={list.refetch}
        retryLabel={t('orders.waiting.retry')}
        retrying={list.isFetching}
        data-testid="waiting-on-blank-error"
      />
    );
  } else if (total === 0 && !searching) {
    body = (
      <EmptyState
        icon={PackageCheck}
        title={t('orders.waiting.empty.title')}
        description={t('orders.waiting.empty.body')}
        action={
          <Button type="button" variant="outline" className={SMALL_BUTTON} onClick={onClose}>
            {t('orders.waiting.backToQueue')}
          </Button>
        }
      />
    );
  } else if (total === 0) {
    body = (
      <div className="flex flex-col items-center gap-2 py-12 text-center" role="status">
        <p className="text-sm text-muted-foreground">{t('orders.waiting.noMatch', { query: search.trim() })}</p>
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm"
          onClick={() => {
            setSearchInput('');
            onSearchChange('');
          }}
        >
          {t('orders.waiting.clearSearch')}
        </Button>
      </div>
    );
  } else {
    const columns = showClient ? 5 : 4;
    body = (
      <Card className="relative overflow-hidden">
        {list.isFetching && list.isPlaceholderData && (
          <div className="pointer-events-none absolute inset-0 z-10 bg-background/40" aria-hidden="true" />
        )}
        {canViewPos && pos.failed && (
          <SectionError
            variant="inline"
            className="m-3"
            body={t('orders.waiting.posError')}
            onRetry={pos.retry}
            retryLabel={t('orders.waiting.retry')}
            retrying={pos.retrying}
          />
        )}
        {/* A size container, so each PO row can pin itself to the visible width (cqw). */}
        <div className="overflow-x-auto [container-type:inline-size]">
          <table className="w-full min-w-[36rem] text-sm">
            <caption className="sr-only">{t('orders.waiting.caption')}</caption>
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="py-2 pl-10 pr-3 font-medium">
                  {t('orders.waiting.col.order')}
                </th>
                {showClient && (
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t('orders.waiting.col.client')}
                  </th>
                )}
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('orders.waiting.col.ordered')}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t('orders.waiting.col.qty')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('orders.waiting.col.status')}
                </th>
              </tr>
            </thead>
            {groups.map((group) => {
              const header = headers.get(group.poId) ?? null;
              return (
                <PoGroup
                  key={group.poId}
                  group={group}
                  header={header}
                  sentToday={header ? sentTodayKeys.has(supplierKey(header)) : false}
                  columns={columns}
                  showClient={showClient}
                  ownOrgId={ownOrgId}
                  orgIdToName={orgIdToName}
                  timezone={timezone}
                  locationId={locationId}
                  canViewPos={canViewPos}
                  canManagePos={canManagePos}
                  onArrived={() => setArrivingPoId(group.poId)}
                />
              );
            })}
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => goToPage(page - 1)}
              disabled={page === 0 || list.isFetching}
            >
              <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
              {t('orders.previous')}
            </Button>
            <span className="text-sm tabular-nums text-muted-foreground">
              {t('orders.waiting.page', { page: page + 1, pages })}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => goToPage(page + 1)}
              disabled={!list.data.hasMore || list.isFetching}
            >
              {t('orders.next')}
              <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        )}
      </Card>
    );
  }

  return (
    <section aria-labelledby="waiting-on-blank-title" className="space-y-4" data-testid="waiting-on-blank-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-prose space-y-1">
          <h2 id="waiting-on-blank-title" className="text-lg font-semibold">
            {t('orders.waiting.title')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('orders.waiting.lede')}</p>
        </div>
        <div className="flex w-full items-center gap-3 sm:w-auto">
          {list.data && total > 0 && (
            <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground" role="status">
              {t('orders.waiting.orderCount', { count: total })}
            </span>
          )}
          <div className="relative w-full sm:w-72">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              maxLength={100}
              placeholder={t('orders.waiting.searchPlaceholder')}
              aria-label={t('orders.waiting.searchLabel')}
              className="h-9 pl-8"
            />
          </div>
        </div>
      </div>

      {body}

      <ArrivedDialog
        open={arriving !== null}
        onOpenChange={(open) => !open && setArrivingPoId(null)}
        po={arriving}
        locationId={locationId}
        nextPoNumber={arrivingNext}
      />
    </section>
  );
}

interface PoGroupProps {
  group: WaitingPoGroup;
  header: OrderAndWaitPo | null;
  /** An order to this PO's supplier went out today at the floor: Send by is tomorrow's. */
  sentToday: boolean;
  columns: number;
  showClient: boolean;
  ownOrgId: string | undefined;
  orgIdToName: ReadonlyMap<string, string>;
  timezone: string | null;
  locationId: string;
  canViewPos: boolean;
  canManagePos: boolean;
  onArrived: () => void;
}

function PoGroup({
  group,
  header,
  sentToday,
  columns,
  showClient,
  ownOrgId,
  orgIdToName,
  timezone,
  locationId,
  canViewPos,
  canManagePos,
  onArrived,
}: PoGroupProps) {
  const { t } = useTranslation('production');
  const locale = useDateFnsLocale();
  const here = useLocation();
  const ordersReturnTo = `${here.pathname}${here.search}`;
  const headingId = `waiting-po-${group.poId}`;
  const supplier = group.supplierName;
  const building = group.status === 'not_sent';

  const title = building
    ? supplier
      ? t('orders.waiting.nextOrder', { supplier })
      : t('orders.waiting.nextOrderNoSupplier')
    : (supplier ?? t('orders.waiting.sentNoSupplier'));

  const meta: string[] = [];
  if (building && header) {
    const sendBy = sendByState(header.sendMode, header.sendTime, timezone, undefined, sentToday);
    if (sendBy) {
      const time = formatSendTime(sendBy.time, locale);
      meta.push(sendBy.tomorrow ? t('orders.waiting.sendByTomorrow', { time }) : t('orders.waiting.sendBy', { time }));
    }
  }
  if (!building && header?.submittedDate) {
    meta.push(t('orders.waiting.sentOn', { date: formatDayMonth(header.submittedDate, locale) }));
  }
  if (header) {
    meta.push(t('orders.waiting.blankCount', { count: header.waitingUnits }));
    meta.push(t('orders.waiting.orderCount', { count: header.waitingOrders }));
  }

  const hint = building
    ? t('orders.waiting.sendingHint')
    : !canManagePos
      ? t('orders.waiting.arrivedHint')
      : null;

  return (
    <tbody aria-labelledby={headingId} className="border-t first:border-t-0" data-testid="waiting-po-group">
      <tr className="bg-muted/30">
        <th scope="rowgroup" colSpan={columns} className="px-3 py-2.5 text-left font-normal">
          {/* Pinned to the scroll box's visible width (the cell's 0.75rem padding
              each side), so a phone that scrolls the orders sideways still sees
              the whole row: stacked below sm, one line above it. */}
          <div className="sticky left-3 w-[calc(100cqw-1.5rem)]">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4">
              <div className="min-w-0 space-y-0.5">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <OrderAndWaitStatusChip status={group.status} dueDate={group.arrivalEtaDate} />
                  <span id={headingId} className="font-semibold">
                    {title}
                    <span className="text-muted-foreground"> · </span>
                    {canViewPos && group.poNumber ? (
                      <PoNumberLink poId={group.poId} poNumber={group.poNumber} ordersReturnTo={ordersReturnTo} />
                    ) : (
                      <span className="font-mono font-normal">{group.poNumber}</span>
                    )}
                  </span>
                </div>
                {meta.length > 0 && <p className="text-xs tabular-nums text-muted-foreground">{meta.join(' · ')}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {building && canViewPos && (
                  <Button asChild variant="outline" className={GROUP_BUTTON}>
                    <Link to={`/purchase-orders?tab=to-buy&loc=${encodeURIComponent(locationId)}`}>
                      {t('orders.waiting.openToBuy')}
                    </Link>
                  </Button>
                )}
                {!building && canManagePos && header && (
                  <Button
                    type="button"
                    variant="outline"
                    className={ARRIVED_BUTTON}
                    onClick={onArrived}
                    aria-describedby={headingId}
                    data-testid="waiting-arrived-button"
                  >
                    <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('orders.waiting.arrived')}
                  </Button>
                )}
              </div>
            </div>
            {hint && <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>}
          </div>
        </th>
      </tr>
      {group.blanks.map((blank) => (
        <Fragment key={blank.key}>
          <tr>
            <td colSpan={columns} className="px-3 pb-1 pl-6 pt-3">
              <span className="font-medium">{jitRowName(blank)}</span>
              {blank.sku && <span className="ml-2 font-mono text-[11px] text-muted-foreground">{blank.sku}</span>}
            </td>
          </tr>
          {blank.orders.map((entry) => {
            const rowOrg = entry.row.organizationId;
            const clientName = rowOrg && rowOrg !== ownOrgId ? (orgIdToName.get(rowOrg) ?? null) : null;
            return (
              <tr key={entry.row.prodOrderId} className="hover:bg-muted/40" data-testid="waiting-order-row">
                <td className="py-2 pl-10 pr-3 font-medium">{entry.row.orderName ?? '-'}</td>
                {showClient && (
                  <td className="px-3 py-2">
                    {clientName ? (
                      <ClientOrgBadge orgName={clientName} />
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                )}
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                  {entry.row.orderDate ? formatDayMonth(entry.row.orderDate, locale) : '-'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{entry.qty}</td>
                <td className="px-3 py-2">
                  <OrderAndWaitStatusChip
                    status={entry.status}
                    dueDate={entry.arrivalEtaDate}
                    supplierName={supplier}
                  />
                </td>
              </tr>
            );
          })}
        </Fragment>
      ))}
    </tbody>
  );
}

/** Section-level load: rows the height of the real ones (DESIGN.md section 15.1). */
function WaitingSkeleton() {
  const { t } = useTranslation('production');
  return (
    <Card className="space-y-2 p-3" role="status" aria-busy="true" aria-label={t('orders.waiting.loading')}>
      <Skeleton className="h-12 w-full" />
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </Card>
  );
}
